import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import SessionPopupDialog from './SessionPopupDialog';
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import { userPool } from '../../store/actions/awsProfile';
import { userSignOut } from '../../store/actions/signOutActions';

// Configuration constants
const TOTAL_SESSION_DURATION = 45 * 60 * 1000; // 45 minutes total session time
const POPUP_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

/**
 * SessionTimeoutManager component
 * Handles user session timeouts and shows a popup before logging out
 * Tracks total session duration
 */
const SessionTimeoutManager = ({ authenticated, userSignOut }) => {
  // console.log('SessionTimeoutManager rendering, authenticated:', authenticated);
  const [showPopup, setShowPopup] = useState(false);

  // Timeout refs
  const totalSessionTimeout = useRef(null);
  const popupTimeout = useRef(null);

  // Tracking refs
  const sessionStartTime = useRef(Date.now());
  const isRefreshing = useRef(false);

  // Page visibility tracking
  const isPageVisible = useRef(true);

  /**
   * Refresh the Cognito session tokens
   * Uses the refresh token to get new access and ID tokens
   */
  const refreshSession = () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing.current) {
      return Promise.reject(new Error('Session refresh already in progress'));
    }

    isRefreshing.current = true;
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      return new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
          if (err) {
            isRefreshing.current = false;
            // console.error('Error getting current session:', err);
            reject(err);
            return;
          }

          if (session && session.isValid()) {
            // Refresh the session token
            cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
              isRefreshing.current = false;

              if (err) {
                // console.error('Error refreshing session:', err);
                reject(err);
                return;
              }

              // Update local storage with new tokens
              localStorage.setItem('user_token', newSession.getAccessToken().getJwtToken());
              localStorage.setItem('Bearer', newSession.getIdToken().getJwtToken());
              // console.log('Session refreshed successfully');
              resolve(newSession);
            });
          } else {
            isRefreshing.current = false;
            reject(new Error('Session is not valid'));
          }
        });
      });
    }

    isRefreshing.current = false;
    return Promise.reject(new Error('No user found'));
  };



  /**
   * Start the total session duration timer
   * This starts a countdown from login that will expire after TOTAL_SESSION_DURATION
   */
  const startTotalSessionTimer = () => {
    if (totalSessionTimeout.current) {
      clearTimeout(totalSessionTimeout.current);
    }

    sessionStartTime.current = Date.now();
    // console.log('Setting total session timeout for', TOTAL_SESSION_DURATION / 1000, 'seconds');

    totalSessionTimeout.current = setTimeout(() => {
      // console.log('Total session timeout triggered, showing popup');
      setShowPopup(true);

      // Set timeout for auto logout if user doesn't respond to the popup
      popupTimeout.current = setTimeout(() => {
        // console.log('Session expired: No response to popup, logging out');
        setShowPopup(false);
        userSignOut();

        // Backup logout
        if (window.location.pathname !== '/login') {
          localStorage.clear();
          window.location.href = '/login';
        }
      }, POPUP_DURATION);
    }, TOTAL_SESSION_DURATION);
  };

  /**
   * Handle page visibility changes
   * Uses the Page Visibility API to detect when the user switches tabs or minimizes browser
   */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      // Page is now hidden
      isPageVisible.current = false;
      // console.log('Page is now hidden, session timer continues in background');
    } else {
      // Page is now visible again
      isPageVisible.current = true;
      // console.log('Page is now visible');

      // Check if total session timeout was reached while page was hidden
      const currentTime = Date.now();
      const sessionElapsed = currentTime - sessionStartTime.current;
      if (sessionElapsed >= TOTAL_SESSION_DURATION &&
        sessionElapsed < TOTAL_SESSION_DURATION + POPUP_DURATION) {
        setShowPopup(true);
      }
    }
  };



  /**
   * Handle the "Continue" button in the timeout dialog
   * This refreshes the session with Cognito and resets the timer
   */
  const handleContinueSession = async () => {
    try {
      // Refresh the Cognito session using REFRESH_TOKEN_AUTH
      await refreshSession();
      setShowPopup(false);

      if (popupTimeout.current) {
        clearTimeout(popupTimeout.current);
      }

      // Reset the total session timer
      startTotalSessionTimer();
    } catch (error) {
      console.error('Failed to refresh session:', error);
      userSignOut();
    }
  };

  /**
   * Handle the "Logout" button in the timeout dialog
   * This logs the user out immediately
   */
  const handleLogout = () => {
    setShowPopup(false);
    // Ensure we're calling the Redux action to properly handle logout
    userSignOut();
    // console.log('Logout initiated from session timeout');

    // Just in case the Redux action isn't working, force logout
    if (window.location.pathname !== '/login') {
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  // Set up event listeners and timers when component mounts or auth state changes
  useEffect(() => {
    // Only set up the listeners if the user is authenticated
    if (authenticated) {
      // console.log('SessionTimeoutManager: Setting up session timeout monitoring');
      // console.log('Authentication state:', authenticated);

      // Set initial timer for total session
      startTotalSessionTimer();

      // Set up Page Visibility API listener
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function
      return () => {
        // console.log('SessionTimeoutManager: Cleaning up session timeout monitoring');

        // Clear all timers
        if (totalSessionTimeout.current) {
          clearTimeout(totalSessionTimeout.current);
        }

        if (popupTimeout.current) {
          clearTimeout(popupTimeout.current);
        }

        // Remove visibility change listener
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [authenticated]); // Only re-run when authentication state changes

  // Log whenever showPopup changes
  useEffect(() => {
    // console.log('showPopup state changed:', showPopup);
  }, [showPopup]);



  // Render the timeout dialog when needed
  return (
    <>
      
      {showPopup && (
        <SessionPopupDialog
          onContinue={handleContinueSession}
          onLogout={handleLogout}
          timeoutDuration={POPUP_DURATION}
        />
      )}
    </>
  );
};

// Connect component to Redux store
const mapStateToProps = (state) => {
  // console.log('Redux auth state:', state.auth);
  return {
    authenticated: state.auth && state.auth.authenticated
  };
};

export default connect(mapStateToProps, { userSignOut })(SessionTimeoutManager);
